import React, { useCallback } from 'react';
import { Gallery, Amenity, GalleryImageItem } from './amenity';
import { isInquiryModeEnabled, onOpenInquiry } from '../state/inquiry-modal';
import { AccommodationType, GalleryImage } from '../constants/accommodation';

export interface AccommodationAmenity {
  title: string;
  description: string;
}

export interface AccommodationContentProps {
  /** Gallery images - can be string URLs or objects with portrait flag */
  galleryImages: GalleryImageItem[] | GalleryImage[];
  galleryAlt: string;
  description: React.ReactNode;
  amenities: AccommodationAmenity[];
  /** Optional services available upon request for an additional fee */
  optionalServices?: AccommodationAmenity[];
  accommodationType?: AccommodationType;
  cta?: string;
  // Quick info for prominent display
  priceDisplay?: string;
  capacity?: number;
  highlights?: string[];
}

export const AccommodationContent: React.FC<AccommodationContentProps> = ({
  galleryImages,
  galleryAlt,
  description,
  amenities,
  optionalServices,
  accommodationType,
  cta = 'Book Now',
  priceDisplay,
  capacity,
  highlights = ['Private bathroom', 'Daily housekeeping', 'All meals included'],
}) => {
  const handleBookingClick = useCallback(() => {
    if (isInquiryModeEnabled) {
      onOpenInquiry({
        accommodationType,
      });
    } else {
      const params = new URLSearchParams();
      if (accommodationType) params.set('tentType', accommodationType);
      window.location.href = `/packages/reservation${params.toString() ? `?${params.toString()}` : ''}`;
    }
  }, [accommodationType]);
  return (
    <div className="space-y-10">
      {/* Quick Info Card - First Glance Information */}
      {(priceDisplay || capacity) && (
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 md:p-8 border border-emerald-100">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            {/* Price & Capacity */}
            <div className="flex flex-wrap items-center gap-6 md:gap-8">
              {priceDisplay && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-emerald-600 font-semibold mb-1">
                    Starting from
                  </p>
                  <p className="text-3xl md:text-4xl font-bold text-emerald-800 font-display">
                    {priceDisplay}
                  </p>
                  <p className="text-sm text-gray-600">per night</p>
                </div>
              )}

              {capacity && (
                <div className="pl-6 md:pl-8 border-l border-emerald-200">
                  <p className="text-xs uppercase tracking-wider text-emerald-600 font-semibold mb-1">
                    Capacity
                  </p>
                  <p className="text-3xl md:text-4xl font-bold text-emerald-800 font-display">
                    {capacity}
                  </p>
                  <p className="text-sm text-gray-600">
                    {capacity === 1 ? 'guest' : 'guests'}
                  </p>
                </div>
              )}
            </div>

            {/* Book Now CTA */}
            <button
              onClick={handleBookingClick}
              className="flex-shrink-0 w-full md:w-auto px-8 py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white rounded-xl uppercase text-sm tracking-wider font-bold shadow-lg shadow-emerald-600/20 hover:shadow-emerald-600/30 transition-all hover:scale-[1.02]"
            >
              {cta} &rarr;
            </button>
          </div>

          {/* Quick Highlights */}
          {highlights.length > 0 && (
            <div className="mt-6 pt-6 border-t border-emerald-200">
              <div className="flex flex-wrap gap-3">
                {highlights.map((highlight, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/80 text-emerald-700 text-sm font-medium rounded-full border border-emerald-200"
                  >
                    <svg
                      className="w-4 h-4 text-emerald-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {highlight}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Gallery Section */}
      <section>
        <h2 className="text-xl font-bold font-display text-emerald-900 mb-5 flex items-center gap-3">
          <svg
            className="w-5 h-5 text-emerald-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          Gallery
        </h2>
        <Gallery images={galleryImages} alt={galleryAlt} />
      </section>

      {/* Description */}
      <section>
        <h2 className="text-xl font-bold font-display text-emerald-900 mb-5 flex items-center gap-3">
          <svg
            className="w-5 h-5 text-emerald-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          About This Accommodation
        </h2>
        <div className="prose prose-emerald prose-lg max-w-none text-gray-700 prose-p:leading-relaxed prose-p:mb-4 prose-strong:text-emerald-800">
          {description}
        </div>
      </section>

      {/* Amenities - What's Included */}
      <section>
        <h2 className="text-xl font-bold font-display text-emerald-900 mb-5 flex items-center gap-3">
          <svg
            className="w-5 h-5 text-emerald-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
          What&apos;s Included
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {amenities.map((amenity, index) => (
            <Amenity
              key={index}
              title={amenity.title}
              description={amenity.description}
              open={index < 2}
            />
          ))}
        </div>
      </section>

      {/* Optional Services - Available Upon Request */}
      {optionalServices && optionalServices.length > 0 && (
        <section>
          <div className="mb-5">
            <h2 className="text-xl font-bold font-display text-amber-900 flex items-center gap-3">
              <svg
                className="w-5 h-5 text-amber-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Available Upon Request
            </h2>
            <p className="text-sm text-amber-700 mt-1 ml-8">
              Enhance your stay with these optional experiences (additional charges apply)
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {optionalServices.map((service, index) => (
              <Amenity
                key={index}
                title={service.title}
                description={service.description}
                variant="addon"
              />
            ))}
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="bg-emerald-800 rounded-2xl p-8 text-center">
        <h3 className="text-2xl font-bold text-white font-display mb-3">
          Ready to Experience Kumbh Sukrit?
        </h3>
        <p className="text-emerald-200 text-sm font-medium mb-2">
          Camp open: 31 Dec 2025 – End of Feb 2026
        </p>
        <p className="text-emerald-100 mb-6 max-w-lg mx-auto">
          {isInquiryModeEnabled
            ? "Register your interest and we'll help you plan your stay for Magh Mela 2026."
            : 'Book now to secure your spot for Magh Mela 2026.'}
        </p>
        <button
          onClick={handleBookingClick}
          className="px-10 py-4 bg-white hover:bg-gray-50 text-emerald-800 rounded-xl uppercase text-sm tracking-wider font-bold shadow-lg transition-all hover:scale-[1.02]"
        >
          {cta} &rarr;
        </button>
      </section>
    </div>
  );
};

// Amenities included with every booking
export const INCLUDED_AMENITIES: AccommodationAmenity[] = [
  {
    title: 'Premium Accommodation',
    description:
      'Comfortable stay with quality bedding, pillows, blankets, and daily housekeeping service.',
  },
  {
    title: 'Three Daily Meals',
    description:
      'Nutritious vegetarian breakfast, lunch, and dinner prepared with fresh ingredients.',
  },
];

// Optional services available upon request for an additional fee
export const OPTIONAL_SERVICES: AccommodationAmenity[] = [
  {
    title: 'Yoga & Meditation',
    description:
      'Daily morning yoga and meditation sessions led by experienced instructors.',
  },
  {
    title: 'Airport Transfers',
    description:
      'Pick-up and drop-off service from Prayagraj Airport or Railway Station.',
  },
  {
    title: 'Triveni Sangam Tour',
    description:
      'Guided tour and boat ride to the sacred confluence of Ganga, Yamuna, and Saraswati.',
  },
  {
    title: 'Sacred Ceremonies',
    description:
      'Participate in traditional Vedic fire ceremonies (Yajna) and ritual guidance.',
  },
  {
    title: 'Gaushala Visit',
    description:
      'Experience the sacred tradition of caring for cows at our on-site Gaushala.',
  },
  {
    title: 'Ashram Visits',
    description:
      'Guided visits to nearby ashrams and spiritual centers with insights into yogic traditions.',
  },
];

// Combined for backward compatibility
export const STANDARD_AMENITIES: AccommodationAmenity[] = [
  ...INCLUDED_AMENITIES,
  ...OPTIONAL_SERVICES,
];

// Highlight presets
export const MUDHOUSE_HIGHLIGHTS = [
  'Eco-luxury accommodation',
  'Natural climate control',
  'Private bathroom',
  'All meals included',
  'Daily housekeeping',
];

export const TENT_HIGHLIGHTS = [
  'Twin beds',
  'Private bathroom',
  'Climate controlled',
  'All meals included',
  'Daily housekeeping',
];

export const DORMITORY_HIGHLIGHTS = [
  'Eco-luxury shared accommodation',
  'Natural climate control',
  'Traditional, large mudhouse',
  'All meals included',
  'Community experience',
];
